import { createContext, type ReactNode, type RefObject, useContext, useEffect, useMemo, useRef } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { PromiseQueue } from '~/classes/PromiseQueue';
import { type SearchTranscriptCache } from '~/swr-hooks/clips/transcript/search/types';
import { isDevOrTestStage } from '~/swr-hooks/utils';

type SearchTranscriptFetchQueueContextType = {
  fetchQueue: RefObject<PromiseQueue<SearchTranscriptCache> | undefined>;
};

const defaultValue: SearchTranscriptFetchQueueContextType = {
  fetchQueue: { current: undefined },
};

export const SearchTranscriptFetchQueueContext = createContext<SearchTranscriptFetchQueueContextType>(defaultValue);

type SearchTranscriptFetchQueueProviderProps = {
  children: ReactNode;
};

export const SearchTranscriptFetchQueueProvider = ({ children }: SearchTranscriptFetchQueueProviderProps) => {
  const fetchQueue = useRef<PromiseQueue<SearchTranscriptCache>>();

  useEffect(() => {
    fetchQueue.current = new PromiseQueue();

    return () => {
      fetchQueue.current?.cancelAll();
    };
  }, []);

  const value = useMemo<SearchTranscriptFetchQueueContextType>(() => ({ fetchQueue }), [fetchQueue]);

  return (
    <SearchTranscriptFetchQueueContext.Provider value={value}>{children}</SearchTranscriptFetchQueueContext.Provider>
  );
};

export const useSearchTranscriptFetchQueueContext = () => {
  const context = useContext(SearchTranscriptFetchQueueContext);

  if (context === defaultValue) {
    const error = 'SearchTranscriptFetchQueueContext used outside of SearchTranscriptFetchQueueProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
